/*!

=========================================================
* Paper Kit React - v1.3.0
=========================================================

* Product Page: https://www.creative-tim.com/product/paper-kit-react

* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/paper-kit-react/blob/main/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from "react";

// reactstrap components
import { Container, Row, Col } from "reactstrap";
import { useRecoilValue } from "recoil";
import { websiteDataState } from "helpers/recoil";
import ReactMarkdown from "react-markdown";

// core components
// import thumbMale from "../../assets/img/ryan.jpg"
// import thumbFemale from "../../assets/img/eva.jpg"

function SectionDark() {
  const websiteData = useRecoilValue(websiteDataState);

  // Function to group people into rows for optimal layout
  const groupPeopleIntoRows = (people) => {
    if (!people || people.length === 0) return [];
    
    const count = people.length;
    
    // Determine how to distribute people across rows
    if (count <= 3) {
      // 1-3 people: single row
      return [people];
    } else if (count === 4) {
      // 4 people: 2 rows of 2
      return [people.slice(0, 2), people.slice(2, 4)];
    } else if (count === 5) {
      // 5 people: 3 in first row, 2 in second
      return [people.slice(0, 3), people.slice(3, 5)];
    } else if (count === 6) {
      // 6 people: 2 rows of 3
      return [people.slice(0, 3), people.slice(3, 6)];
    } else if (count === 7) {
      // 7 people: 4 in first row, 3 in second
      return [people.slice(0, 4), people.slice(4, 7)];
    } else if (count === 8) {
      // 8 people: 2 rows of 4
      return [people.slice(0, 4), people.slice(4, 8)];
    } else if (count === 9) {
      // 9 people: 3 rows of 3
      return [people.slice(0, 3), people.slice(3, 6), people.slice(6, 9)];
    } else {
      // 10+ people: distribute evenly (max 4 per row)
      const rows = [];
      const peoplePerRow = 4;
      for (let i = 0; i < people.length; i += peoplePerRow) {
        rows.push(people.slice(i, i + peoplePerRow));
      }
      return rows;
    }
  };

  // Function to determine column size based on number of people in row
  const getColSize = (peopleInRow) => {
    if (peopleInRow === 1) return { sm: "12", md: "8", lg: "6" };
    if (peopleInRow === 2) return { sm: "12", md: "6", lg: "6" };
    if (peopleInRow === 3) return { sm: "12", md: "4", lg: "4" };
    if (peopleInRow === 4) return { sm: "6", md: "3", lg: "3" };
    return { sm: "6", md: "4", lg: "3" };
  };

  const peopleRows = groupPeopleIntoRows(websiteData?.people || []);

  return (
    <>
      <div className="section" style={{ 
        padding: "80px 0",
        background: "linear-gradient(180deg, #f8f9fa 0%, #ffffff 100%)"
      }}>
        <Container>
          <Row>
            <Col className="ms-auto me-auto text-center" md="10" lg="8">
              <h2 className="title" style={{ 
                fontSize: "36px", 
                fontWeight: "700",
                color: "#2d3748",
                marginBottom: "32px",
                letterSpacing: "-0.5px",
                lineHeight: "1.2"
              }}>
                {websiteData?.snippetsDictionary?.["section-header-team"]?.text}
              </h2>
              <div style={{ 
                fontSize: "16px", 
                lineHeight: "1.7", 
                color: "#4a5568",
                marginBottom: "40px"
              }}>
                <ReactMarkdown>
                  {
                    websiteData?.snippetsDictionary?.["section-body-team"]?.markup
                      ?.markdown
                  }
                </ReactMarkdown>
              </div>
              <Col sm="12" style={{ marginBottom: "40px" }}>
                <img
                  className="img-responsive"
                  style={{ 
                    borderRadius: "12px",
                    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
                    width: "100%",
                    transition: "transform 0.3s ease"
                  }}
                  src={
                    websiteData?.snippetsDictionary?.["section-photo-team"]
                      ?.photo?.url
                  }
                  alt="Kine Team"
                />
              </Col>
              {peopleRows.map((row, rowIndex) => (
                <Row
                  key={`row-${rowIndex}`}
                  style={{ paddingTop: "30px" }}
                  className="justify-content-md-center justify-content-lg-center"
                >
                  {row.map((person, personIndex) => {
                    const colSize = getColSize(row.length);
                    return (
                      <Col
                        key={`person-${rowIndex}-${personIndex}`}
                        sm={colSize.sm}
                        md={colSize.md}
                        lg={colSize.lg}
                        style={{ marginBottom: "30px" }}
                      >
                        {person?.picture?.url && (
                          <div style={{ 
                            borderRadius: "12px",
                            overflow: "hidden",
                            boxShadow: "0 8px 25px rgba(0, 0, 0, 0.1)",
                            transition: "all 0.3s ease",
                            cursor: "pointer"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-8px)";
                            e.currentTarget.style.boxShadow = "0 12px 35px rgba(0, 0, 0, 0.15)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "0 8px 25px rgba(0, 0, 0, 0.1)";
                          }}>
                            <img
                              className="img-responsive"
                              style={{ width: "100%", display: "block" }}
                              src={person?.picture?.url}
                              alt={person.name}
                            />
                          </div>
                        )}
                        <p style={{ 
                          fontWeight: "600", 
                          fontSize: "16px",
                          color: "#2d3748",
                          marginTop: "16px",
                          marginBottom: "6px",
                          letterSpacing: "-0.2px"
                        }}>{person.name}</p>
                        <p style={{ 
                          color: "#69be50",
                          fontWeight: "500",
                          fontSize: "14px",
                          lineHeight: "1.4"
                        }}>{person?.specialtities}</p>
                      </Col>
                    );
                  })}
                </Row>
              ))}
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
}

export default SectionDark;
